/**
 * HTML Content Utilities
 * Common functions for handling HTML content encoding/decoding
 */

import { marked } from 'marked';
import { AI_HTML_CLASSES, AI_GWT_STYLES, GWT_KEYWORDS } from './appConstants';
import { sanitizeRichTextHtml } from './richTextSanitizer';

/**
 * Detects whether a plain-text string contains Markdown syntax.
 * Returns true when at least one common Markdown pattern is found.
 */
export const isMarkdown = (text) => {
  if (!text) return false;
  return /^#{1,6}\s|^[*-]\s|^\d+\.\s|^```|\*\*[^*]+\*\*|__[^_]+__|\*[^*]+\*|_[^_]+_|`[^`]+`|\[.+\]\(.+\)/m.test(text);
};

/**
 * Converts a Markdown string to sanitized HTML using `marked`.
 * Strips dangerous tags so the result is safe to insert into the editor.
 */
export const markdownToHtml = (markdown) => {
  if (!markdown) return '';
  const raw = marked.parse(markdown, { async: false });
  // Strip script/style/iframe tags for safety
  return raw
    .replace(/<script[^>]*>.*?<\/script>/gi, '')
    .replace(/<style[^>]*>.*?<\/style>/gi, '')
    .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '');
};

// Helper function to decode HTML entities (handles multiple levels of encoding)
export const decodeHtmlContent = (htmlString) => {
  if (!htmlString) return '';
  
  let decoded = htmlString;
  let previousDecoded = '';
  let iterations = 0;
  const maxIterations = 5; // Prevent infinite loops
  
  // Keep decoding until no more changes or max iterations reached
  while (decoded !== previousDecoded && iterations < maxIterations) {
    previousDecoded = decoded;
    decoded = decoded
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&nbsp;/g, ' ')
      .replace(/&#160;/g, ' ')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
    iterations++;
  }
  
  return decoded;
};

// Helper function to safely render HTML content for editing
export const prepareContentForEditing = (htmlString) => {
  if (!htmlString) return '';
  if (htmlString.includes('data-pmpi-raw-html="true"')) {
    return htmlString;
  }
  if (htmlString.includes('&lt;') || htmlString.includes('&gt;')) {
    return decodeHtmlContent(htmlString);
  }
  return htmlString;
};

const sanitizeHtmlMarkup = (htmlString) => {
  return sanitizeRichTextHtml(htmlString, { source: 'render' });
};

// Helper function to safely render comment content by creating DOM elements
export const renderCommentContent = (htmlString, mentionUsers = []) => {
  if (!htmlString) return '';
  
  // Check if content contains code blocks
  const hasCodeBlocks = htmlString.includes('[CODE_START_MARKER]') && htmlString.includes('[CODE_END_MARKER]');
  
  if (hasCodeBlocks) {
    // Create a temporary container to build the content
    const container = document.createElement('div');
    
    // Split content by code markers
    const parts = htmlString.split(/\[CODE_START_MARKER\]([^\[\]]+?)\[CODE_END_MARKER\]/g);
    
    const appendHtmlFragment = (html) => {
      if (!html.trim()) return;
      const wrapper = document.createElement('div');
      wrapper.innerHTML = html;
      while (wrapper.firstChild) {
        container.appendChild(wrapper.firstChild);
      }
    };

    for (let i = 0; i < parts.length; i++) {
      if (i % 2 === 0) {
        // Regular content (not code)
        appendHtmlFragment(renderHtmlContentWithMentions(parts[i], mentionUsers));
      } else {
        // Code content - create safe code block
        const codeBlock = document.createElement('div');
        codeBlock.className = 'pmpi-code-block';
        
        const pre = document.createElement('pre');
        const code = document.createElement('code');
        
        // Set text content directly (this auto-escapes HTML)
        code.textContent = parts[i];
        
        pre.appendChild(code);
        codeBlock.appendChild(pre);
        container.appendChild(codeBlock);
      }
    }
    
    return container.innerHTML;
  }
  
  // Regular content - use existing function
  return renderHtmlContentWithMentions(htmlString, mentionUsers);
};

// Helper function to safely render HTML content for display
export const renderHtmlContent = (htmlString) => {
  if (!htmlString) return '';

  // Preserve editor-marked raw HTML snippets as literal text while still sanitizing the rest.
  if (htmlString.includes('data-pmpi-raw-html="true"')) {
    if (typeof DOMParser === 'undefined') {
      return htmlString;
    }
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(`<div>${htmlString}</div>`, 'text/html');
      const container = doc.body.firstChild;
      if (!container) return htmlString;

      container.querySelectorAll('[data-pmpi-raw-html="true"]').forEach((el) => {
        el.removeAttribute('data-pmpi-raw-html');
      });

      return formatAcceptanceCriteria(sanitizeHtmlMarkup(container.innerHTML));
    } catch (error) {
      return htmlString;
    }
  }

  // First decode HTML entities if they exist
  let decoded = htmlString;
  if (htmlString.includes('&lt;') || htmlString.includes('&gt;')) {
    decoded = decodeHtmlContent(htmlString);
  }

  return formatAcceptanceCriteria(sanitizeHtmlMarkup(decoded));
};

export const renderHtmlContentWithMentions = (htmlString, mentionUsers = []) => {
  const rendered = renderHtmlContent(htmlString);
  if (!rendered) return '';
  if (!Array.isArray(mentionUsers) || mentionUsers.length === 0) return rendered;
  if (typeof DOMParser === 'undefined') return rendered;
  if (!rendered.includes('mention')) return rendered;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${rendered}</div>`, 'text/html');
    const container = doc.body.firstChild;
    if (!container) return rendered;

    const userMap = new Map();
    mentionUsers.forEach((user) => {
      if (!user) return;
      const id = user.id || user.user_id || user.value || user.email || user.name;
      const name = user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.label;
      if (!id || !name) return;
      userMap.set(String(id), name);
    });

    container.querySelectorAll('span.mention[data-user-id]').forEach((span) => {
      const id = span.getAttribute('data-user-id');
      const matchedName = id ? userMap.get(String(id)) : null;
      if (matchedName) {
        span.textContent = `@${matchedName}`;
      } else {
        span.classList.remove('mention');
        span.removeAttribute('data-user-id');
      }
    });

    return container.innerHTML;
  } catch (error) {
    return rendered;
  }
};

/**
 * Transforms checkbox patterns into numbered list items within acceptance criteria sections.
 * Pattern: <p>[ ]</p><p>criteria text</p> → <li>criteria text</li>
 * Also handles [x] for checked items.
 */
const formatAcceptanceCriteria = (htmlString) => {
  if (!htmlString.includes(AI_HTML_CLASSES.ACCEPTANCE_CRITERIA_BODY)) return htmlString;

  const classPattern = new RegExp(
    `(<div\\s+class="${AI_HTML_CLASSES.ACCEPTANCE_CRITERIA_BODY}">)([\\s\\S]*?)(</div>)`,
    'gi'
  );

  return htmlString.replace(classPattern, (match, openTag, bodyContent, closeTag) => {
    // Pair each <p>[ ]</p> or <p>[x]</p> marker with the following <p> into <li>
    let formatted = bodyContent.replace(
      /<p>\s*\[\s*x?\s*\]\s*<\/p>\s*<p>([\s\S]*?)<\/p>/gi,
      '<li>$1</li>'
    );

    // Wrap consecutive <li> items in <ol>
    formatted = formatted.replace(
      /((?:<li>[\s\S]*?<\/li>\s*)+)/gi,
      '<ol>$1</ol>'
    );

    // 2. Format Given/When/Then scenario blocks
    const gwtKeywordsPattern = Object.values(GWT_KEYWORDS).join('|');
    const gwtRegex = new RegExp(`^(?:(?:\\d+[\\)\\.]\\s*)?)(${gwtKeywordsPattern})\\b(.*?)$`, 'i');
    
    formatted = formatted.replace(/<p>([\s\S]*?)<\/p>/gi, (pMatch, pContent) => {
      const textContent = pContent.trim();
      // Match optional number/bullet and then Given/When/Then/And/But using generic constant map
      const gwtMatch = textContent.match(gwtRegex);
      
      if (gwtMatch) {
        const keyword = gwtMatch[1]; // Given, When, Then, And, But
        const restText = gwtMatch[2] ? gwtMatch[2].trim() : '';
        
        const upperKeyword = keyword.toUpperCase();
        const styleConfig = AI_GWT_STYLES[upperKeyword] || AI_GWT_STYLES.DEFAULT;
        
        const badgeBg = styleConfig.bg;
        const badgeColor = styleConfig.color;
        const badgeBorder = styleConfig.border;
        
        const isNewGroup = upperKeyword === GWT_KEYWORDS.GIVEN;
        const marginStyle = isNewGroup ? 'margin-top: 24px; margin-bottom: 8px;' : 'margin-bottom: 8px;';
        
        return `
<div class="ac-history-gwt-section" style="display: flex; align-items: flex-start; ${marginStyle}">
  <span class="ac-history-chip-${keyword.toLowerCase()}" style="background: ${badgeBg}; color: ${badgeColor}; border: ${badgeBorder}; padding: 2px 8px; border-radius: 12px; font-size: 12px; font-weight: 600; margin-right: 12px; height: fit-content; line-height: 1.5; min-width: 60px; text-align: center; display: inline-block;">${keyword}</span>
  <div class="ac-history-gwt-text" style="margin: 0; font-size: 14px; color: #1e293b; line-height: 1.5; padding-top: 2px;">${restText}</div>
</div>
        `.trim();
      }
      return pMatch;
    });

    return openTag + formatted + closeTag;
  });
};

// Normalize "visually empty" rich-text HTML to empty string so we don't send junk like "<br>".
// Common editors may output: "<br>", "<p><br></p>", "<p>&nbsp;</p>", etc. when the field is cleared.
export const normalizeRichTextHtml = (htmlString) => {
  if (!htmlString) return '';

  // Keep behavior consistent with existing decode path.
  const decoded = prepareContentForEditing(htmlString);

  // If content contains media or interactive elements (images, figures, etc.), it is not visually empty
  if (/<(img|figure|svg|video|audio|iframe)\b/i.test(decoded)) {
    return decoded;
  }

  // Fast path: if, after stripping tags + whitespace, nothing remains => empty.
  const normalized = decoded
    .replace(/&nbsp;/gi, ' ')
    .replace(/\u00a0/g, ' ')
    // Convert common line breaks to spaces before stripping tags
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/p>\s*<p[^>]*>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  return normalized.length === 0 ? '' : decoded;
};

/**
 * Strips HTML tags and script/style blocks, converting common block boundaries to newlines,
 * and returns clean plain text.
 */
export const stripHtmlToPlainText = (htmlString) => {
  if (!htmlString) return '';
  const decoded = decodeHtmlContent(htmlString);
  if (!decoded) return '';

  return decoded
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<(br|\/p|\/div|\/li|\/h[1-6])\b[^>]*>/gi, '\n')
    .replace(/<li\b[^>]*>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
};

/**
 * Normalizes and sanitizes pasted content across editing surfaces (titles, comments, descriptions).
 * Centralizes text hygiene and prevents semantic drift between modals and inputs.
 *
 * @param {string|Object} rawInput - HTML string, plain text, or ClipboardEvent
 * @param {Object} [options]
 * @param {'title'|'comment'} [options.mode='title'] - Target surface mode:
 *   - 'title': Converts multi-line HTML, rich text, or plain text into a sanitized, single-line string.
 *              Strips screenshot containers, images, and HTML tags while inserting spaces around block boundaries.
 *              Decodes HTML entities, removes zero-width/control characters, and collapses multiple whitespace.
 *   - 'comment': Sanitizes HTML using DOMPurify / richTextSanitizer for safe rich-text comments.
 * @returns {string} Sanitized and normalized output
 */
export const normalizePastedText = (rawInput, { mode = 'title' } = {}) => {
  if (rawInput == null) return '';

  let content = '';
  if (typeof rawInput === 'object') {
    const clipboardData = rawInput.clipboardData || rawInput;
    if (typeof clipboardData?.getData === 'function') {
      const html = clipboardData.getData('text/html');
      const plain = clipboardData.getData('text/plain') || clipboardData.getData('text');
      content = html || plain || '';
    } else {
      content = String(rawInput);
    }
  } else {
    content = String(rawInput);
  }

  if (!content) return '';

  if (mode === 'title') {
    // 1. Remove style, script, and comments entirely
    let text = content
      .replace(/<!--[\s\S]*?-->/g, ' ')
      .replace(/<style[\s\S]*?<\/style>/gi, ' ')
      .replace(/<script[\s\S]*?<\/script>/gi, ' ');

    // 2. Remove inline screenshot containers, figures, and raw image tags
    text = text
      .replace(/<figure[^>]*class="[^"]*pmpi-inline-screenshot[^"]*"[\s\S]*?<\/figure>/gi, ' ')
      .replace(/<figure\b[\s\S]*?<\/figure>/gi, ' ')
      .replace(/<img\b[^>]*>/gi, ' ');

    // 3. Convert block boundaries, headings, list items, and table rows to spaces to avoid smashing words together
    text = text
      .replace(/<(br|\/p|\/div|\/li|\/h[1-6]|\/tr|\/table|\/blockquote|hr)\b[^>]*>/gi, ' ')
      .replace(/<(p|div|li|h[1-6]|tr|td|th|blockquote)\b[^>]*>/gi, ' ');

    // 4. Strip remaining HTML tags
    text = text.replace(/<[^>]+>/g, ' ');

    // 5. Decode HTML entities (e.g., &amp; -> &, &quot; -> ", &nbsp; -> space)
    text = decodeHtmlContent(text);

    // 6. Normalize newlines, carriage returns, tabs, zero-width spaces, and NBSP
    text = text
      .replace(/[\u00a0\u2000-\u200B\u2028\u2029\uFEFF]/g, ' ')
      .replace(/[\r\n\t]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    return text;
  }

  if (mode === 'comment') {
    return sanitizeRichTextHtml(content, { source: 'paste' });
  }

  return content;
};


